// frontend/src/pages/components/SperrlisteImportDialog.tsx
//
// 2-Step-Wizard für Sperrliste-Import:
//   Step 0: Upload (.xlsx oder .csv) → POST /sperrliste/import-preview
//   Step 1: Mapping-Bestätigung mit Dropdown pro Spalte → POST /sperrliste/import-confirm
//   Step 2: Erfolgs-/Fehler-Anzeige (analog Vorgänger-Dialog)
//
// Hintergrund: Quick-Task 260528-prw. Backend liefert detected_mapping als
// Vorschlag (z.B. KAT→inf1, INF1→kategorie); User kann pro Spalte einen
// anderen Ziel-DB-Feld wählen oder die Spalte ignorieren (leerer String).
import { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, LinearProgress, Alert, Box, Stepper, Step, StepLabel,
  Select, MenuItem, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, FormControl, Paper
} from '@mui/material';
import { getApiUrl, authFetch } from '../../config';

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}

interface PreviewData {
  session_id: string;
  headers: string[] | null;
  sample_rows: string[][];
  detected_mapping: Record<string, string>;
  total_rows: number;
  db_columns: string[];
}

interface ImportResult {
  imported: number;
  errors: number;
  error_details: string[];
}

const STEPS = ['Datei wählen', 'Mapping bestätigen', 'Import läuft'];

export default function SperrlisteImportDialog({ open, onClose, onDone }: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState('');

  const reset = () => {
    setActiveStep(0);
    setFile(null);
    setPreview(null);
    setMapping({});
    setResult(null);
    setError('');
    setPreviewing(false);
    setImporting(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setError('');
    setPreview(null);
  };

  // Step 0 → Step 1: Upload + Parse via Preview-Endpoint
  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);

      // NOTE zu authFetch: setzt KEINE Default-Content-Type. Für FormData
      // wird vom Browser automatisch multipart/form-data mit Boundary gesetzt
      // — gut so, also keine eigenen Header nötig.
      const res = await authFetch(getApiUrl('/sperrliste/import-preview'), {
        method: 'POST',
        body: formData,
      });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        throw new Error('Server-Fehler beim Upload — bitte erneut versuchen');
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Preview fehlgeschlagen');

      const p: PreviewData = {
        session_id: data.session_id,
        headers: data.headers,
        sample_rows: data.sample_rows || [],
        detected_mapping: data.detected_mapping || {},
        total_rows: data.total_rows,
        db_columns: data.db_columns || [],
      };
      setPreview(p);
      setMapping({ ...p.detected_mapping });
      setActiveStep(1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPreviewing(false);
    }
  };

  // Step 1 → Step 2: Confirm-Endpoint + Job-Polling
  const handleConfirm = async () => {
    if (!preview) return;
    setImporting(true);
    setError('');
    setActiveStep(2);
    try {
      // /import-confirm erwartet JSON-Body — wir setzen Content-Type explizit,
      // weil authFetch keine Defaults injiziert.
      const res = await authFetch(getApiUrl('/sperrliste/import-confirm'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: preview.session_id,
          mapping,
        }),
      });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        throw new Error('Server-Fehler beim Confirm — bitte erneut versuchen');
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Confirm fehlgeschlagen');

      // Async job — poll status (analog Legacy-Dialog)
      const jobId = data.job_id;
      while (true) {
        await new Promise(r => setTimeout(r, 3000));
        const statusRes = await authFetch(getApiUrl(`/sperrliste/import/jobs/${jobId}`));
        const statusData = await statusRes.json();
        if (!statusData.success) throw new Error(statusData.error);
        const job = statusData.job;
        if (job.status === 'done') {
          setResult({
            imported: job.imported,
            errors: job.errors,
            error_details: job.error_details || [],
          });
          break;
        }
        if (job.status === 'error') {
          throw new Error(job.message || 'Import fehlgeschlagen');
        }
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    setPreview(null);
    setMapping({});
    setError('');
  };

  const handleClose = () => {
    if (result) onDone();
    else onClose();
    reset();
  };

  // Anzahl der Spalten und Beispielwerte für Step 1
  // Dropdown-Optionen = preview.db_columns + alle aktuell im Mapping verwendeten
  // Werte (Backend liefert für additional_data-Felder semantische Field-Namen
  // wie 'fns', 'flg1', 'extra', die nicht in db_columns stehen — sonst zeigt
  // MUI Select das Feld leer an).
  const dropdownOptions = useMemo<string[]>(() => {
    if (!preview) return [];
    const set = new Set<string>(preview.db_columns);
    for (const v of Object.values(mapping)) {
      if (typeof v === 'string') set.add(v);
    }
    return Array.from(set);
  }, [preview, mapping]);

  const columnCount = preview
    ? (preview.headers?.length ?? preview.sample_rows[0]?.length ?? 0)
    : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Sperrliste Import (CSV / Excel)</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {/* Step 0: Datei wählen */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Lade eine .xlsx- oder .csv-Datei hoch. Im nächsten Schritt kannst du das
              Spalten-Mapping prüfen und bei Bedarf korrigieren, bevor der Import startet.
              Der Import ist additiv — bestehende Einträge bleiben erhalten.
            </Typography>
            <input
              type="file"
              accept=".csv,.txt,.xlsx"
              onChange={handleFileChange}
              style={{ marginBottom: '1rem' }}
              disabled={previewing}
            />
            {file && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
            {previewing && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Datei wird geparst…</Typography>
                <LinearProgress />
              </Box>
            )}
          </Box>
        )}

        {/* Step 1: Mapping bestätigen */}
        {activeStep === 1 && preview && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`${preview.total_rows.toLocaleString('de-DE')} Datensätze gefunden`}
                color="primary"
              />
              <Chip label={`${columnCount} Spalten`} variant="outlined" />
              {!preview.headers && (
                <Chip
                  label="Keine Header erkannt — Mapping bitte manuell setzen"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Die meisten Spalten wurden automatisch zugeordnet. Unbekannte Header landen
              als Zusatzdaten im JSON-Feld <Box component="code" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>additional_data</Box> unter dem
              Spaltennamen. Wähle <Box component="code" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>(ignorieren)</Box>, wenn eine Spalte nicht importiert werden soll.
            </Typography>
            <Paper variant="outlined" sx={{ maxHeight: 420, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '25%' }}>Spalte</TableCell>
                    <TableCell sx={{ width: '30%' }}>Mapping →</TableCell>
                    <TableCell>Beispielwerte</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: columnCount }).map((_, idx) => {
                    const headerLabel = preview.headers?.[idx] || `Spalte ${idx + 1}`;
                    const colKey = String(idx);
                    const examples = preview.sample_rows
                      .map(r => r[idx])
                      .filter(v => v !== undefined && v !== null && String(v).trim() !== '')
                      .slice(0, 3);
                    return (
                      <TableRow key={colKey}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {headerLabel}
                          </Typography>
                          {preview.headers && (
                            <Typography variant="caption" color="text.secondary">
                              Pos {idx + 1}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={mapping[colKey] ?? ''}
                              onChange={(e) =>
                                setMapping(m => ({ ...m, [colKey]: e.target.value as string }))
                              }
                              displayEmpty
                            >
                              {dropdownOptions.map(col => (
                                <MenuItem key={col || '__empty'} value={col}>
                                  {col === '' ? '(ignorieren)' : col}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}
                          >
                            {examples.length > 0 ? examples.join(' | ') : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {/* Step 2: Import läuft / fertig */}
        {activeStep === 2 && (
          <Box>
            {importing && !result && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Import läuft… ({preview?.total_rows.toLocaleString('de-DE')} Datensätze)
                </Typography>
                <LinearProgress />
              </Box>
            )}
            {result && (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {result.imported.toLocaleString('de-DE')} Einträge importiert
                  {result.errors > 0 && `, ${result.errors} Fehler`}
                </Alert>
                {result.error_details.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {result.error_details.map((d, i) => (
                      <Typography key={i} variant="caption" display="block">{d}</Typography>
                    ))}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions>
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose} disabled={previewing}>Abbrechen</Button>
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={!file || previewing}
            >
              Weiter
            </Button>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={handleBack} disabled={importing}>Zurück</Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={importing || !preview}
            >
              Import starten
            </Button>
          </>
        )}
        {activeStep === 2 && (
          <Button onClick={handleClose} disabled={importing && !result}>
            {result ? 'Schließen' : 'Abbrechen'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
